import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { ChangeDetectionStrategy, Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, TemplateRef, ViewChild, ViewContainerRef } from '@angular/core';

import { AutocompleteField, AutocompleteItem } from '../../models/autocomplete_item.model';

@Component({
  host: { 'test-id': 'fw-autocomplete' },
  selector: 'fw-autocomplete',
  styleUrls: ['./fw-autocomplete.component.scss'],
  templateUrl: './fw-autocomplete.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FwAutocompleteComponent implements OnChanges, OnDestroy {
  @Input() items: AutocompleteItem[];
  @Input() width: string;
  @Input() maxHeight: string;
  @Input() showAutocomplete = true;

  @Output() readonly itemSelected = new EventEmitter<AutocompleteField>();

  @ViewChild('dropdownTemplate') readonly dropdownTemplate: TemplateRef<unknown>;
  @ViewChild('childDropdownTemplate') readonly childDropdownTemplate: TemplateRef<unknown>;

  @HostListener('mousedown', ['$event'])
  onMouseDown(): void {
    this.isMouseEvent = true;
  }


  @HostListener('document:keydown', ['$event'])
  handleKeydown(event: KeyboardEvent): void {
    this.isMouseEvent = false;

    if (!this.overlayRef) {
      return;
    }

    if (event.key === 'Tab') {
      this.closeDropdown();
    } else if (event.key === 'ArrowDown') {
      if (this.childOverlayRef && this.currentHoveredChildItem) {
        this.navigateChildItems(1);
      } else {
        this.navigateItems(1);
      }
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === 'ArrowUp') {
      if (this.childOverlayRef && this.currentHoveredChildItem) {
        this.navigateChildItems(-1);
      } else {
        this.navigateItems(-1);
      }
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === 'ArrowRight') {
      this.openChildMenu();

      if (this.currentHoveredItem.subfields && this.currentHoveredItem.subfields.length > 0) {
        this.currentHoveredChildItem = this.currentHoveredItem.subfields[0];
      }

      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === 'ArrowLeft') {
      this.closeChildMenu();
      event.preventDefault();
      event.stopPropagation();
    } else if (event.key === 'Enter') {
      if (this.childOverlayRef && this.currentHoveredChildItem) {
        this.selectCurrentChildItem();
      } else {
        this.selectCurrentItem();
      }

      this.closeDropdown();
    }
  }

  private overlayRef: OverlayRef | null = null;
  private childOverlayRef: OverlayRef | null = null;

  private readonly mouseLeaveHandler: (event: MouseEvent) => void;

  protected isMouseEvent = false;
  protected currentHoveredItem: AutocompleteItem = null;
  protected currentHoveredChildItem: AutocompleteField = null;
  protected readonly dataChildOverlayAttribute = 'data-child-overlay';
  protected readonly dataChildOverlayItem = 'data-child-overlay-item';

  constructor(private overlay: Overlay, private viewContainerRef: ViewContainerRef, private elementRef: ElementRef) {
    this.mouseLeaveHandler = this.onMouseLeaveAutocomplete.bind(this);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.items?.currentValue !== changes.items?.previousValue && !changes.items?.firstChange) {
      this.closeChildDropdown();
      this.currentHoveredItem = null;
    }
  }

  ngOnDestroy(): void {
    this.removeEventListeners();
  }

  openDropdown(): void {
    if (!this.overlayRef) {
      const positionStrategy = this.overlay.position()
          .flexibleConnectedTo(this.elementRef)
          .withPositions([{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]);

      this.overlayRef = this.overlay.create({
        positionStrategy,
        hasBackdrop: true,
        backdropClass: 'cdk-overlay-transparent-backdrop',
      });

      this.overlayRef.backdropClick().subscribe(() => {
        this.closeDropdown();
      });
    }

    if (this.overlayRef && !this.overlayRef.hasAttached()) {
      const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
      this.overlayRef.attach(portal);

      this.overlayRef.overlayElement.addEventListener('mouseleave', this.mouseLeaveHandler);
    }
  }

  protected openDropdownOnFocus(): void {
    if (this.isMouseEvent) {
      return;
    }

    this.openDropdown();
  }

  protected closeDropdown(): void {
    if (this.overlayRef) {
      this.overlayRef.detach();
      this.overlayRef.overlayElement.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }

    this.currentHoveredItem = null;
    this.closeChildDropdown();
  }

  protected openChildDropdown(item: AutocompleteItem, event?: MouseEvent): void {
    this.currentHoveredItem = item;
    if (!this.childOverlayRef) {
      const positionStrategy = this.overlay.position()
          .flexibleConnectedTo(event ? event.target as HTMLElement : this.elementRef)
          .withPositions([{ originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top' }]);

      this.childOverlayRef = this.overlay.create({ positionStrategy });
    }

    if (this.childOverlayRef && !this.childOverlayRef.hasAttached()) {
      const portal = new TemplatePortal(this.childDropdownTemplate, this.viewContainerRef);
      this.childOverlayRef.attach(portal);

      this.childOverlayRef.overlayElement.setAttribute(this.dataChildOverlayAttribute, 'true');
      this.childOverlayRef.overlayElement.addEventListener('mouseleave', this.mouseLeaveHandler);
    }
  }

  protected closeChildDropdown(): void {
    if (this.childOverlayRef) {
      this.childOverlayRef.detach();
      this.childOverlayRef.overlayElement.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }

    this.childOverlayRef = null;
    this.currentHoveredChildItem = null;
  }

  protected onMouseLeaveAutocomplete(event: MouseEvent): void {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget && relatedTarget.getAttribute(this.dataChildOverlayAttribute) !== 'true' && relatedTarget.getAttribute(this.dataChildOverlayItem) !== 'true') {
      this.closeChildDropdown();
    }
  }

  protected onItemHover(item: AutocompleteItem, event: MouseEvent): void {
    if (this.currentHoveredItem !== item) {
      this.closeChildDropdown();
    }

    if (!this.childOverlayRef) {
      this.openChildDropdown(item, event);
    }
  }

  protected onChildHover(item: AutocompleteField): void {
    this.currentHoveredChildItem = item;
  }

  protected trackAutocompleteItem(index: number, item: AutocompleteItem): string {
    return index + item.field.name;
  }

  protected trackAutocompleteField(index: number, item: AutocompleteField): string {
    return index + item.name;
  }

  protected selectItem(item: AutocompleteField): void {
    this.itemSelected.emit(item);
    this.closeDropdown();
  }

  private navigateItems(direction: number): void {
    if (this.items && this.items.length > 0) {
      const currentIndex = this.items.findIndex(item => item.field.value === this.currentHoveredItem?.field.value);
      const nextIndex = (currentIndex + direction + this.items.length) % this.items.length;
      this.currentHoveredItem = this.items[nextIndex];
    }
  }

  private navigateChildItems(direction: number): void {
    if (this.currentHoveredItem.subfields && this.currentHoveredItem.subfields.length > 0) {
      const currentIndex = this.currentHoveredItem.subfields.indexOf(this.currentHoveredChildItem);
      const nextIndex = (currentIndex + direction + this.currentHoveredItem.subfields.length) % this.currentHoveredItem.subfields.length;
      this.currentHoveredChildItem = this.currentHoveredItem.subfields[nextIndex];
    }
  }

  private selectCurrentItem(): void {
    if (this.currentHoveredItem) {
      this.selectItem(this.currentHoveredItem.field);
    }
  }

  private selectCurrentChildItem(): void {
    if (this.currentHoveredChildItem) {
      this.selectItem(this.currentHoveredChildItem);
    }
  }

  private openChildMenu(): void {
    if (this.currentHoveredItem && this.currentHoveredItem.subfields && this.currentHoveredItem.subfields.length > 0) {
      this.openChildDropdown(this.currentHoveredItem);
    }
  }

  private closeChildMenu(): void {
    if (this.childOverlayRef) {
      this.closeChildDropdown();
    }
  }

  private removeEventListeners(): void {
    if (this.overlayRef) {
      this.overlayRef.overlayElement.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }
    if (this.childOverlayRef) {
      this.childOverlayRef.overlayElement.removeEventListener('mouseleave', this.mouseLeaveHandler);
    }
  }
}

